import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import * as authApi from '@/features/auth/api/auth.api'
import { AUTH_STORAGE_KEY } from '@/features/auth/constants/auth.constants'
import {
  clearAuthTokens,
  setAuthTokens,
} from '@/features/auth/utils/tokenStorage'
import { getDashboardPathForRole, isValidRole } from '@/shared/constants/roles'
import {
  getCurrentFcmToken,
  setupFcmNotifications,
  setupForegroundNotifications,
} from '@/shared/lib/firebaseMessaging'
import { toast } from '@/shared/lib/toast'
import socketService from '@/shared/socket/socket.service'

const initialState = {
  user: null,
  isAuthenticated: false,
  isBootstrapping: true,
  isLoading: false,
  error: null,
}

export const useAuthStore = create(
  persist(
    (set, get) => ({
      ...initialState,

      setUser: (user) =>
        set({
          user,
          isAuthenticated: Boolean(user),
          error: null,
        }),

      clearError: () => set({ error: null }),

      setBootstrapping: (isBootstrapping) => set({ isBootstrapping }),

      sendOtp: async (email) => {
        set({ isLoading: true, error: null })

        try {
          const result = await authApi.sendOtp(email)

          set({ isLoading: false })

          toast.success(result.message || 'OTP sent successfully')

          return result
        } catch (error) {
          const message = error.message || 'Failed to send OTP'

          set({
            isLoading: false,
            error: message,
          })

          toast.error(message)

          throw error
        }
      },

      login: async ({ email, otp }) => {
        set({
          isLoading: true,
          error: null,
        })

        try {
          const result = await authApi.login({
            email,
            otp,
          })

          const payload = result.data || {}
          const { accessToken, refreshToken, ...user } = payload

          setAuthTokens({ accessToken, refreshToken })

          set({
            user,
            isAuthenticated: true,
            isBootstrapping: false,
            isLoading: false,
            error: null,
          })

          void setupFcmNotifications().catch((error) => {
            console.error(
              'Failed to setup FCM notifications during login',
              error,
            )
          })
          void setupForegroundNotifications().catch((error) => {
            console.error(
              'Failed to setup foreground notifications during login',
              error,
            )
          })

          return {
            user,
            redirectTo: getDashboardPathForRole(user.role),
          }
        } catch (error) {
          const message = error.message || 'Login failed'

          clearAuthTokens()

          set({
            isLoading: false,
            error: message,
          })

          toast.error(message)

          throw error
        }
      },

      logout: async () => {
        set({
          isLoading: true,
          error: null,
        })

        try {
          const fcmToken = await getCurrentFcmToken()

          const result = await authApi.logout(fcmToken)

          toast.success(result?.message || 'Signed out successfully')
        } catch (error) {
          toast.error(error.message || 'Signed out locally')
        } finally {
          socketService.disconnect()
          clearAuthTokens()

          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          })
        }
      },

      bootstrap: async () => {
        const { user } = get()

        if (!user?.role || !isValidRole(user.role)) {
          clearAuthTokens()
          set({
            user: null,
            isAuthenticated: false,
            isBootstrapping: false,
          })

          return
        }

        set({
          isBootstrapping: true,
          error: null,
        })

        try {
          const result = await authApi.fetchProfileByRole(user.role)

          const profile = result.data

          set({
            user: {
              ...profile,
              role: profile.role || user.role,
            },
            isAuthenticated: true,
            isBootstrapping: false,
          })

          void setupFcmNotifications().catch((err) =>
            console.error('Bootstrap FCM setup error:', err),
          )
          void setupForegroundNotifications().catch((err) =>
            console.error('Bootstrap foreground setup error:', err),
          )
        } catch {
          clearAuthTokens()
          set({
            user: null,
            isAuthenticated: false,
            isBootstrapping: false,
          })
        }
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      partialize: (state) => ({
        user: state.user
          ? {
              id: state.user.id,
              email: state.user.email,
              name: state.user.name,
              role: state.user.role,
            }
          : null,
      }),
    },
  ),
)
